// src/pages/finance-portal/category-mapping/CategoryMapping.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Autocomplete,
    Slider,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Skeleton,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RuleIcon from '@mui/icons-material/Rule';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function TabPanel({ children, value, index }) {
    return value === index ? <Box>{children}</Box> : null;
}

export default function CategoryMapping() {
    const { enqueueSnackbar } = useSnackbar();

    const [tabValue, setTabValue] = useState(0);

    // ---- Category Mappings State ----
    const [mappings, setMappings] = useState([]);
    const [mappingsLoading, setMappingsLoading] = useState(true);
    const [glAccounts, setGlAccounts] = useState([]);
    const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
    const [editingMapping, setEditingMapping] = useState(null);
    const [mappingForm, setMappingForm] = useState({ category_primary: '', category_detailed: '', gl_account_id: null, confidence: 0.8 });
    const [savingMapping, setSavingMapping] = useState(false);
    const [deletingMappingId, setDeletingMappingId] = useState(null);
    const [autoCategorizing, setAutoCategorizing] = useState(false);
    const [autoCatResult, setAutoCatResult] = useState(null);
    const [detectingDuplicates, setDetectingDuplicates] = useState(false);
    const [duplicateResult, setDuplicateResult] = useState(null);

    // ---- Transaction Rules State ----
    const [rules, setRules] = useState([]);
    const [rulesLoading, setRulesLoading] = useState(true);
    const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [ruleForm, setRuleForm] = useState({ name: '', priority: 0, is_active: true, conditions: [], actions: [] });
    const [savingRule, setSavingRule] = useState(false);
    const [deletingRuleId, setDeletingRuleId] = useState(null);
    const [applyingRules, setApplyingRules] = useState(false);

    // ---- Fetch GL Accounts ----
    const fetchGlAccounts = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/finance/coa?flat=true');
            if (response.data.success) {
                setGlAccounts(response.data.data || []);
            }
        } catch (err) {
            // silent
        }
    }, []);

    // ---- Category Mappings ----
    const fetchMappings = useCallback(async () => {
        setMappingsLoading(true);
        try {
            const response = await axiosInstance.get('/api/finance/plaid/category-mappings');
            if (response.data.success) {
                setMappings(response.data.data || []);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to load category mappings', { variant: 'error' });
        } finally {
            setMappingsLoading(false);
        }
    }, [enqueueSnackbar]);

    // ---- Transaction Rules ----
    const fetchRules = useCallback(async () => {
        setRulesLoading(true);
        try {
            const response = await axiosInstance.get('/api/finance/plaid/transaction-rules');
            if (response.data.success) {
                setRules(response.data.data || []);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to load transaction rules', { variant: 'error' });
        } finally {
            setRulesLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        fetchGlAccounts();
        fetchMappings();
        fetchRules();
    }, [fetchGlAccounts, fetchMappings, fetchRules]);

    // ---- Mapping Handlers ----
    const openAddMapping = () => {
        setEditingMapping(null);
        setMappingForm({ category_primary: '', category_detailed: '', gl_account_id: null, confidence: 0.8 });
        setMappingDialogOpen(true);
    };

    const openEditMapping = (mapping) => {
        setEditingMapping(mapping);
        setMappingForm({
            category_primary: mapping.category_primary || '',
            category_detailed: mapping.category_detailed || '',
            gl_account_id: mapping.gl_account_id || null,
            confidence: mapping.confidence ?? 0.8,
        });
        setMappingDialogOpen(true);
    };

    const handleSaveMapping = async () => {
        if (!mappingForm.category_primary) {
            enqueueSnackbar('Primary category is required', { variant: 'warning' });
            return;
        }
        setSavingMapping(true);
        try {
            if (editingMapping) {
                const response = await axiosInstance.put(`/api/finance/plaid/category-mappings/${editingMapping.id}`, mappingForm);
                if (response.data.success) {
                    enqueueSnackbar('Mapping updated', { variant: 'success' });
                }
            } else {
                const response = await axiosInstance.post('/api/finance/plaid/category-mappings', mappingForm);
                if (response.data.success) {
                    enqueueSnackbar('Mapping created', { variant: 'success' });
                }
            }
            setMappingDialogOpen(false);
            fetchMappings();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save mapping', { variant: 'error' });
        } finally {
            setSavingMapping(false);
        }
    };

    const handleDeleteMapping = async (id) => {
        setDeletingMappingId(id);
        try {
            const response = await axiosInstance.delete(`/api/finance/plaid/category-mappings/${id}`);
            if (response.data.success) {
                enqueueSnackbar('Mapping deleted', { variant: 'success' });
                fetchMappings();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete mapping', { variant: 'error' });
        } finally {
            setDeletingMappingId(null);
        }
    };

    const handleAutoCategorize = async () => {
        setAutoCategorizing(true);
        setAutoCatResult(null);
        try {
            const response = await axiosInstance.post('/api/finance/plaid/auto-categorize');
            if (response.data.success) {
                setAutoCatResult(response.data.data || response.data);
                enqueueSnackbar(`Auto-categorization complete: ${response.data.data?.categorized || response.data.categorized || 0} transactions updated`, { variant: 'success' });
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Auto-categorize failed', { variant: 'error' });
        } finally {
            setAutoCategorizing(false);
        }
    };

    const handleDetectDuplicates = async () => {
        setDetectingDuplicates(true);
        setDuplicateResult(null);
        try {
            const response = await axiosInstance.post('/api/finance/plaid/detect-duplicates');
            if (response.data.success) {
                setDuplicateResult(response.data.data || response.data);
                enqueueSnackbar(`Duplicate detection complete: ${response.data.data?.duplicates_found || response.data.duplicates_found || 0} duplicates found`, { variant: 'info' });
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Duplicate detection failed', { variant: 'error' });
        } finally {
            setDetectingDuplicates(false);
        }
    };

    // ---- Rule Handlers ----
    const openAddRule = () => {
        setEditingRule(null);
        setRuleForm({ name: '', priority: 0, is_active: true, conditions: [], actions: [] });
        setRuleDialogOpen(true);
    };

    const openEditRule = (rule) => {
        setEditingRule(rule);
        // Migrate legacy 'merchant_name' conditions to 'merchant_or_name'
        const migratedConditions = Array.isArray(rule.conditions)
            ? rule.conditions.map(c => c.field === 'merchant_name' ? { ...c, field: 'merchant_or_name' } : { ...c })
            : [];
        setRuleForm({
            name: rule.name || '',
            priority: rule.priority ?? 0,
            is_active: rule.is_active ?? true,
            conditions: migratedConditions,
            actions: Array.isArray(rule.actions) ? [...rule.actions] : [],
        });
        setRuleDialogOpen(true);
    };

    const handleSaveRule = async () => {
        if (!ruleForm.name) {
            enqueueSnackbar('Rule name is required', { variant: 'warning' });
            return;
        }
        setSavingRule(true);
        try {
            if (editingRule) {
                const response = await axiosInstance.put(`/api/finance/plaid/transaction-rules/${editingRule.id}`, ruleForm);
                if (response.data.success) {
                    enqueueSnackbar('Rule updated', { variant: 'success' });
                }
            } else {
                const response = await axiosInstance.post('/api/finance/plaid/transaction-rules', ruleForm);
                if (response.data.success) {
                    enqueueSnackbar('Rule created', { variant: 'success' });
                }
            }
            setRuleDialogOpen(false);
            fetchRules();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save rule', { variant: 'error' });
        } finally {
            setSavingRule(false);
        }
    };

    const handleDeleteRule = async (id) => {
        setDeletingRuleId(id);
        try {
            const response = await axiosInstance.delete(`/api/finance/plaid/transaction-rules/${id}`);
            if (response.data.success) {
                enqueueSnackbar('Rule deleted', { variant: 'success' });
                fetchRules();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete rule', { variant: 'error' });
        } finally {
            setDeletingRuleId(null);
        }
    };

    const handleApplyRules = async () => {
        setApplyingRules(true);
        try {
            const response = await axiosInstance.post('/api/finance/plaid/apply-rules');
            if (response.data.success) {
                const { applied = 0, matched = 0, total_uncategorized = 0, total_rules = 0 } = response.data.data || {};
                if (total_uncategorized === 0) {
                    enqueueSnackbar('No uncategorized transactions found to apply rules to.', { variant: 'info' });
                } else if (applied === 0 && matched === 0) {
                    enqueueSnackbar(`No rules matched any of the ${total_uncategorized} uncategorized transactions. Check your rule conditions.`, { variant: 'warning' });
                } else if (applied === 0 && matched > 0) {
                    enqueueSnackbar(`${matched} transactions matched but could not be updated. Check your rule actions have a valid GL Account selected.`, { variant: 'warning' });
                } else {
                    enqueueSnackbar(`Successfully categorized ${applied} of ${total_uncategorized} uncategorized transactions.`, { variant: 'success' });
                }
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to apply rules', { variant: 'error' });
        } finally {
            setApplyingRules(false);
        }
    };

    // ---- Condition/Action Builders ----
    const addCondition = () => {
        setRuleForm(prev => ({
            ...prev,
            conditions: [...prev.conditions, { field: 'merchant_or_name', operator: 'contains', value: '' }],
        }));
    };

    const updateCondition = (index, key, value) => {
        setRuleForm(prev => {
            const updated = [...prev.conditions];
            updated[index] = { ...updated[index], [key]: value };
            return { ...prev, conditions: updated };
        });
    };

    const removeCondition = (index) => {
        setRuleForm(prev => ({
            ...prev,
            conditions: prev.conditions.filter((_, i) => i !== index),
        }));
    };

    const addAction = () => {
        setRuleForm(prev => ({
            ...prev,
            actions: [...prev.actions, { set_field: 'gl_account_id', value: '' }],
        }));
    };

    const updateAction = (index, key, value) => {
        setRuleForm(prev => {
            const updated = [...prev.actions];
            updated[index] = { ...updated[index], [key]: value };
            return { ...prev, actions: updated };
        });
    };

    const removeAction = (index) => {
        setRuleForm(prev => ({
            ...prev,
            actions: prev.actions.filter((_, i) => i !== index),
        }));
    };

    const conditionFields = [
        { value: 'merchant_or_name', label: 'Merchant / Name' },
        { value: 'name', label: 'Transaction Name' },
        { value: 'amount', label: 'Amount' },
        { value: 'category_primary', label: 'Primary Category' },
    ];

    const conditionOperators = [
        { value: 'contains', label: 'Contains' },
        { value: 'equals', label: 'Equals' },
        { value: 'gt', label: 'Greater Than' },
        { value: 'lt', label: 'Less Than' },
    ];

    const actionFields = [
        { value: 'gl_account_id', label: 'GL Account ID' },
    ];

    function truncateJson(obj, maxLen = 60) {
        const str = JSON.stringify(obj);
        return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
    }

    function formatConditions(conditions) {
        if (!Array.isArray(conditions) || conditions.length === 0) return '—';
        const fieldLabels = { merchant_or_name: 'Merchant/Name', merchant_name: 'Merchant/Name', name: 'Name', amount: 'Amount', category_primary: 'Category' };
        const opLabels = { contains: 'contains', equals: '=', gt: '>', lt: '<' };
        return conditions.map(c => `${fieldLabels[c.field] || c.field} ${opLabels[c.operator] || c.operator} "${c.value}"`).join(' AND ');
    }

    function formatActions(actions) {
        if (!Array.isArray(actions) || actions.length === 0) return '—';
        return actions.map(a => {
            if (a.set_field === 'gl_account_id') {
                const acct = glAccounts.find(g => g.id === Number(a.value));
                return acct ? `GL → ${acct.account_number} - ${acct.name}` : `GL → ID ${a.value}`;
            }
            return `${a.set_field} → ${a.value}`;
        }).join(', ');
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CategoryIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Category Mapping & Rules
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Map categories to GL accounts and create transaction rules
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Tabs */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5, overflow: 'hidden' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_, v) => setTabValue(v)}
                        sx={{
                            borderBottom: '1px solid #e2e8f0',
                            '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.9rem' },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { bgcolor: ACCENT },
                        }}
                    >
                        <Tab icon={<CategoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Category Mappings" />
                        <Tab icon={<RuleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Transaction Rules" />
                    </Tabs>
                </Paper>

                {/* ========== TAB 1: Category Mappings ========== */}
                <TabPanel value={tabValue} index={0}>
                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openAddMapping}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px',
                                bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                            }}
                        >
                            Add Mapping
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={autoCategorizing ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                            onClick={handleAutoCategorize}
                            disabled={autoCategorizing}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px',
                                borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                            }}
                        >
                            {autoCategorizing ? 'Categorizing...' : 'Auto-Categorize'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={detectingDuplicates ? <CircularProgress size={16} /> : <ContentCopyIcon />}
                            onClick={handleDetectDuplicates}
                            disabled={detectingDuplicates}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px',
                                borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' },
                            }}
                        >
                            {detectingDuplicates ? 'Detecting...' : 'Detect Duplicates'}
                        </Button>
                    </Box>

                    {/* Result Alerts */}
                    {autoCatResult && (
                        <Alert severity="success" onClose={() => setAutoCatResult(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                            Auto-categorization complete: {autoCatResult.categorized || 0} transactions categorized.
                        </Alert>
                    )}
                    {duplicateResult && (
                        <Alert severity="info" onClose={() => setDuplicateResult(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                            Duplicate detection complete: {duplicateResult.duplicates_found || 0} potential duplicates found.
                        </Alert>
                    )}

                    {/* Mappings Table */}
                    <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Plaid Category (Primary)', 'Detailed', 'GL Account', 'Confidence', 'Actions'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mappingsLoading ? (
                                        [...Array(5)].map((_, i) => (
                                            <TableRow key={i}>
                                                {[...Array(5)].map((_, j) => (
                                                    <TableCell key={j}><Skeleton variant="text" sx={{ fontFamily }} /></TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : mappings.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                                                <CategoryIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No category mappings yet</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        mappings.map((mapping) => {
                                            const glAccount = glAccounts.find(g => g.id === mapping.gl_account_id);
                                            const confidence = mapping.confidence ?? 0;
                                            const confColor = confidence >= 0.8 ? ACCENT : confidence >= 0.5 ? '#f59e0b' : '#ef4444';

                                            return (
                                                <TableRow key={mapping.id} sx={{ '&:hover': { bgcolor: '#f0fdf4' } }}>
                                                    <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
                                                        {mapping.category_primary?.replace(/_/g, ' ') || '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                                                        {mapping.category_detailed?.replace(/_/g, ' ') || '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                                                        {glAccount ? `${glAccount.account_number} - ${glAccount.name}` : '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <Chip
                                                            label={`${Math.round(confidence * 100)}%`}
                                                            size="small"
                                                            sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, height: 24, bgcolor: `${confColor}15`, color: confColor }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="Edit">
                                                                <IconButton size="small" onClick={() => openEditMapping(mapping)} sx={{ color: '#64748b', '&:hover': { color: ACCENT } }}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteMapping(mapping.id)}
                                                                    disabled={deletingMappingId === mapping.id}
                                                                    sx={{ color: '#64748b', '&:hover': { color: '#ef4444' } }}
                                                                >
                                                                    {deletingMappingId === mapping.id ? (
                                                                        <CircularProgress size={16} />
                                                                    ) : (
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    )}
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </TabPanel>

                {/* ========== TAB 2: Transaction Rules ========== */}
                <TabPanel value={tabValue} index={1}>
                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openAddRule}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px',
                                bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                            }}
                        >
                            Add Rule
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={applyingRules ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            onClick={handleApplyRules}
                            disabled={applyingRules}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px',
                                borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                            }}
                        >
                            {applyingRules ? 'Applying...' : 'Apply Rules'}
                        </Button>
                    </Box>

                    {/* Rules Table */}
                    <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Name', 'Priority', 'Conditions', 'Actions', 'Active', 'Edit / Delete'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rulesLoading ? (
                                        [...Array(4)].map((_, i) => (
                                            <TableRow key={i}>
                                                {[...Array(6)].map((_, j) => (
                                                    <TableCell key={j}><Skeleton variant="text" sx={{ fontFamily }} /></TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : rules.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                                <RuleIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No transaction rules yet</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        rules.map((rule) => (
                                            <TableRow key={rule.id} sx={{ '&:hover': { bgcolor: '#f0fdf4' } }}>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
                                                    {rule.name || '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                                                    <Chip
                                                        label={rule.priority ?? 0}
                                                        size="small"
                                                        sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, height: 24, bgcolor: '#f1f5f9', color: '#475569' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9', maxWidth: 250 }}>
                                                    <Tooltip title={formatConditions(rule.conditions)}>
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {formatConditions(rule.conditions)}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9', maxWidth: 250 }}>
                                                    <Tooltip title={formatActions(rule.actions)}>
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {formatActions(rule.actions)}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <Chip
                                                        label={rule.is_active ? 'Active' : 'Inactive'}
                                                        size="small"
                                                        sx={{
                                                            fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                            bgcolor: rule.is_active ? '#dcfce7' : '#f1f5f9',
                                                            color: rule.is_active ? '#166534' : '#94a3b8',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => openEditRule(rule)} sx={{ color: '#64748b', '&:hover': { color: ACCENT } }}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteRule(rule.id)}
                                                                disabled={deletingRuleId === rule.id}
                                                                sx={{ color: '#64748b', '&:hover': { color: '#ef4444' } }}
                                                            >
                                                                {deletingRuleId === rule.id ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </TabPanel>
            </Box>

            {/* ========== Mapping Dialog ========== */}
            <Dialog
                open={mappingDialogOpen}
                onClose={() => setMappingDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {editingMapping ? 'Edit Category Mapping' : 'Add Category Mapping'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <TextField
                            label="Primary Category"
                            value={mappingForm.category_primary}
                            onChange={(e) => setMappingForm(prev => ({ ...prev, category_primary: e.target.value }))}
                            fullWidth
                            size="small"
                            placeholder="e.g. FOOD_AND_DRINK"
                            sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />
                        <TextField
                            label="Detailed Category"
                            value={mappingForm.category_detailed}
                            onChange={(e) => setMappingForm(prev => ({ ...prev, category_detailed: e.target.value }))}
                            fullWidth
                            size="small"
                            placeholder="e.g. RESTAURANTS"
                            sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />
                        <Autocomplete
                            options={glAccounts}
                            getOptionLabel={(opt) => opt ? `${opt.account_number} - ${opt.name}` : ''}
                            value={glAccounts.find(g => g.id === mappingForm.gl_account_id) || null}
                            onChange={(_, val) => setMappingForm(prev => ({ ...prev, gl_account_id: val?.id || null }))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="GL Account"
                                    size="small"
                                    placeholder="Select GL Account..."
                                    sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                                />
                            )}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        />
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', mb: 1 }}>
                                Confidence: {Math.round(mappingForm.confidence * 100)}%
                            </Typography>
                            <Slider
                                value={mappingForm.confidence}
                                onChange={(_, val) => setMappingForm(prev => ({ ...prev, confidence: val }))}
                                min={0}
                                max={1}
                                step={0.05}
                                sx={{
                                    color: ACCENT,
                                    '& .MuiSlider-thumb': { bgcolor: ACCENT },
                                    '& .MuiSlider-track': { bgcolor: ACCENT },
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setMappingDialogOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveMapping}
                        disabled={savingMapping}
                        startIcon={savingMapping ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                        }}
                    >
                        {savingMapping ? 'Saving...' : editingMapping ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ========== Rule Dialog ========== */}
            <Dialog
                open={ruleDialogOpen}
                onClose={() => setRuleDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {editingRule ? 'Edit Transaction Rule' : 'Add Transaction Rule'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Rule Name"
                                value={ruleForm.name}
                                onChange={(e) => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                size="small"
                                sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                            />
                            <TextField
                                label="Priority"
                                type="number"
                                value={ruleForm.priority}
                                onChange={(e) => setRuleForm(prev => ({ ...prev, priority: parseInt(e.target.value, 10) || 0 }))}
                                size="small"
                                sx={{ width: 120, fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={ruleForm.is_active}
                                        onChange={(e) => setRuleForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Active</Typography>}
                            />
                        </Box>

                        {/* Conditions Builder */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Conditions</Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={addCondition}
                                    sx={{ fontFamily, textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                                >
                                    Add Condition
                                </Button>
                            </Box>
                            {ruleForm.conditions.length === 0 ? (
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                    No conditions added. Click "Add Condition" to start.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {ruleForm.conditions.map((cond, i) => (
                                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                                <InputLabel sx={{ fontFamily }}>Field</InputLabel>
                                                <Select
                                                    value={cond.field}
                                                    label="Field"
                                                    onChange={(e) => updateCondition(i, 'field', e.target.value)}
                                                    sx={{ fontFamily, borderRadius: '8px' }}
                                                >
                                                    {conditionFields.map(f => (
                                                        <MenuItem key={f.value} value={f.value} sx={{ fontFamily }}>{f.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                                <InputLabel sx={{ fontFamily }}>Operator</InputLabel>
                                                <Select
                                                    value={cond.operator}
                                                    label="Operator"
                                                    onChange={(e) => updateCondition(i, 'operator', e.target.value)}
                                                    sx={{ fontFamily, borderRadius: '8px' }}
                                                >
                                                    {conditionOperators.map(o => (
                                                        <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                size="small"
                                                label="Value"
                                                value={cond.value}
                                                onChange={(e) => updateCondition(i, 'value', e.target.value)}
                                                sx={{ flex: 1, fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily } }}
                                            />
                                            <IconButton size="small" onClick={() => removeCondition(i)} sx={{ color: '#ef4444' }}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* Actions Builder */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Actions</Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={addAction}
                                    sx={{ fontFamily, textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                                >
                                    Add Action
                                </Button>
                            </Box>
                            {ruleForm.actions.length === 0 ? (
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                    No actions added. Click "Add Action" to start.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {ruleForm.actions.map((action, i) => (
                                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                                <InputLabel sx={{ fontFamily }}>Set Field</InputLabel>
                                                <Select
                                                    value={action.set_field}
                                                    label="Set Field"
                                                    onChange={(e) => updateAction(i, 'set_field', e.target.value)}
                                                    sx={{ fontFamily, borderRadius: '8px' }}
                                                >
                                                    {actionFields.map(f => (
                                                        <MenuItem key={f.value} value={f.value} sx={{ fontFamily }}>{f.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            {action.set_field === 'gl_account_id' ? (
                                                <Autocomplete
                                                    options={glAccounts}
                                                    getOptionLabel={(opt) => opt ? `${opt.account_number} - ${opt.name}` : ''}
                                                    value={glAccounts.find(g => g.id === Number(action.value)) || null}
                                                    onChange={(_, val) => updateAction(i, 'value', val?.id ? String(val.id) : '')}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="GL Account"
                                                            placeholder="Select GL Account..."
                                                            sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily } }}
                                                        />
                                                    )}
                                                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                                    sx={{ flex: 1 }}
                                                />
                                            ) : (
                                                <TextField
                                                    size="small"
                                                    label="Value"
                                                    value={action.value}
                                                    onChange={(e) => updateAction(i, 'value', e.target.value)}
                                                    sx={{ flex: 1, fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily } }}
                                                />
                                            )}
                                            <IconButton size="small" onClick={() => removeAction(i)} sx={{ color: '#ef4444' }}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRuleDialogOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveRule}
                        disabled={savingRule}
                        startIcon={savingRule ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                        }}
                    >
                        {savingRule ? 'Saving...' : editingRule ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
